import React, { useState } from 'react';
import { Card, Avatar, Tag, Space, Button, Popconfirm, Tooltip } from 'antd';
import {
  UserOutlined,
  MedicineBoxOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { BsHospital } from 'react-icons/bs';
import { IoIosPaper } from 'react-icons/io';
import { getImg } from '../../../Api/getImg';
import dayjs from 'dayjs';

const ConsultantCard = ({
  item,
  index,
  isMobile,
  onClick,
  onEdit,
  onDelete,
  role,
  deleteLoading,
  isOnVacation = false,
  vacationInfo = null,
}) => {
  const [copiedSlug, setCopiedSlug] = useState(false);

  const joinDate = new Date(item.joinDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const clinicCount = item.clinics ? item.clinics.length : 0;

  const handleCopySlug = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(item.slug);
    setCopiedSlug(true);
    setTimeout(() => setCopiedSlug(false), 2000);
  };

  // Build vacation tooltip label when doctor is on vacation
  const vacationLabel =
    isOnVacation && vacationInfo?.startDate && vacationInfo?.endDate
      ? `On vacation: ${dayjs(vacationInfo.startDate).format('MMM D')} – ${dayjs(vacationInfo.endDate).format('MMM D, YYYY')}`
      : 'On Vacation';

  return (
    <div
      className={`group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden ${isMobile ? 'mb-4' : ''}`}
      style={{
        animationDelay: `${index * 100}ms`,
        animation: 'fadeInUp 0.6s ease-out forwards',
      }}
    >
      <div className="p-4 sm:p-6 border-primary/20 border">

        {/* ── Avatar + Name ── */}
        <div className={`flex items-center mb-4 ${isMobile ? 'flex-wrap' : ''}`}>
          {/* Avatar with vacation ring */}
          <span
            className={`p-[1px] border-2 rounded-full flex justify-center items-center ${
              isOnVacation ? 'border-orange-400 bg-orange-400' : 'border-primary'
            }`}
          >
            <Avatar
              src={
                getImg(item.photoId) ||
                'https://res.cloudinary.com/dk2uh7sho/image/upload/v1750278754/default-img_tbc2pe.jpg'
              }
              size={isMobile ? 40 : 70}
            >
              {!getImg(item.photoId) && item.name?.charAt(0)?.toUpperCase()}
            </Avatar>
          </span>

          <div className="ml-3 sm:ml-4 min-w-0 flex-1">
            <h3
              className={`font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300 ${
                isMobile ? 'text-base' : 'text-lg'
              } mb-1`}
            >
              {item.name}
            </h3>
            <div
              className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-sm'} flex items-center`}
            >
              <MedicineBoxOutlined className="mr-1" />
              {item.specialization}
            </div>
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="p-3 sm:p-4 rounded-lg bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-100 mb-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span
                className={`text-gray-600 font-medium ${isMobile ? 'text-xs' : 'text-sm'} flex items-center`}
              >
                <CalendarOutlined className="mr-2 text-blue-500" />
                Joined
              </span>
              <span
                className={`text-gray-800 font-semibold ${isMobile ? 'text-xs' : 'text-sm'}`}
              >
                {joinDate}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span
                className={`text-gray-600 font-medium ${isMobile ? 'text-xs' : 'text-sm'} flex items-center`}
              >
                <BsHospital className="mr-2 text-green-500" />
                Clinics
              </span>
              <Tag color={clinicCount > 0 ? 'green' : 'default'} className="rounded-full">
                {clinicCount} {clinicCount === 1 ? 'Clinic' : 'Clinics'}
              </Tag>
            </div>

            <div className="flex items-center justify-between">
              <span
                className={`text-gray-600 font-medium ${isMobile ? 'text-xs' : 'text-sm'} flex items-center`}
              >
                <UserOutlined className="mr-2 text-green-500" />
                Patients
              </span>
              <Tag color={item.patientsCount > 0 ? 'green' : 'default'} className="rounded-full">
                {item.patientsCount || 0}
              </Tag>
            </div>

            <div className="flex items-center justify-between">
              <span
                className={`text-gray-600 font-medium ${isMobile ? 'text-xs' : 'text-sm'} flex items-center`}
              >
                <IoIosPaper className="mr-2 text-green-500" />
                Bookings
              </span>
              <Tag color={item.bookingCount > 0 ? 'green' : 'default'} className="rounded-full">
                {item.bookingCount || 0}
              </Tag>
            </div>
          </div>
        </div>

        {/* ── Status row ── */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            {/* Active status */}
            

            {isOnVacation ? (
              <Tooltip title={vacationLabel}>
                <Tag
                  color="orange"
                  icon={<ClockCircleOutlined />}
                  className="rounded-full cursor-default"
                >
                  On Vacation
                </Tag>
              </Tooltip>
            ):<Tag color="green" icon={<CheckCircleOutlined />} className="rounded-full">
              Active
            </Tag>}
          </div>

          {/* Slug copy */}
          {item.slug && (
            <div
              className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-sm'} flex items-center gap-2 relative`}
            >
              <span>{item.slug}</span>
              {copiedSlug && (
                <div className="absolute bottom-8 right-0 bg-gray-800 text-white px-2 py-1 rounded text-xs">
                  Copied!
                </div>
              )}
              <button
                onClick={handleCopySlug}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Copy slug"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* ── Action Buttons ── */}
        {role!=3||role!=7&&
        <Space direction="horizontal" className="w-full" size="small">
          <Button
            type=""
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onClick(item);
            }}
            className="bg-primary hover:bg-primary_hover text-white border-0 rounded-lg font-medium hover:shadow-lg"
            size={isMobile ? 'small' : 'middle'}
          >{
            !isMobile &&           <span className=" ">View Performance </span>  

          }
          </Button>

          <Tooltip title="Edit Consultant">
            <Button
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(item);
              }}
              className="flex-1 border-primary text-primary hover:bg-primary hover:text-white rounded-lg font-medium transition-all"
              size={isMobile ? 'small' : 'middle'}
            />
          </Tooltip>

          <Popconfirm
            title="Delete Consultant"
            description="Are you sure you want to delete this consultant?"
            onConfirm={(e) => {
              e?.stopPropagation();
              onDelete(item.doctorId);
            }}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete Consultant">
              <Button
                icon={<DeleteOutlined />}
                danger
                loading={deleteLoading}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 rounded-lg font-medium"
                size={isMobile ? 'small' : 'middle'}
              />
            </Tooltip>
          </Popconfirm>
        </Space>}
      </div>

      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl" />
    </div>
  );
};

export default ConsultantCard;